import React, { useMemo } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { ALL_TRADES } from '../../../constants/mockData/allTrades';
import { getTradePickerLabel } from '../../../constants/tradeTheme';
import {
  ServiceTypeFilterContainer,
  FilterLabel,
  ServiceTypeFilter as StyledServiceTypeFilter,
} from './ServiceTypeFilter.styled';

const KNOWN_TRADE_IDS = new Set(ALL_TRADES.map((t) => t.id));

const tradeToOption = (trade) => ({
  value: trade.id,
  label: getTradePickerLabel(trade.id),
});

const ServiceTypeFilter = ({
  value = [],
  onChange,
  businesses = [],
  label = 'Trade',
}) => {
  /** Trade-level options (`value` = API `ServiceType` slug, `label` = display name). */
  const serviceTypeOptions = useMemo(() => {
    if (!businesses.length) {
      return ALL_TRADES.map(tradeToOption);
    }

    const tradeIdsOnBusinesses = new Set();
    businesses.forEach((business) => {
      (business.Services || []).forEach((service) => {
        const id = service.ServiceType;
        if (id && KNOWN_TRADE_IDS.has(id)) {
          tradeIdsOnBusinesses.add(id);
        }
      });
    });

    return ALL_TRADES.filter((t) => tradeIdsOnBusinesses.has(t.id)).map(tradeToOption);
  }, [businesses]);

  return (
    <ServiceTypeFilterContainer>
      <FilterLabel variant="subtitle1" component="label">
        {label}
      </FilterLabel>
      <StyledServiceTypeFilter>
        <Autocomplete
          multiple
          options={serviceTypeOptions}
          value={value}
          onChange={(event, newValue) => {
            onChange(newValue);
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={value.length ? '' : 'All trades'}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: '40px',
                  padding: '4px 8px',
                  '& .MuiAutocomplete-input': {
                    padding: '8px 4px 8px 0px',
                  },
                },
                '& .MuiAutocomplete-tag': {
                  margin: '2px',
                },
              }}
            />
          )}
          sx={{
            width: '100%',
            '& .MuiAutocomplete-inputRoot': {
              flexWrap: 'wrap',
              minHeight: '40px',
              padding: '4px 8px',
            },
          }}
        />
      </StyledServiceTypeFilter>
    </ServiceTypeFilterContainer>
  );
};

export default ServiceTypeFilter;
